import "./bootstrap";
import { createApp, defineAsyncComponent } from "vue";
import Notifications from "@kyvg/vue3-notification";
import VCalendar from "v-calendar";
import "@fortawesome/fontawesome-free/css/all.css";

// --- Vue components (keep all for now) ---
import GenericContext from "./components/GenericContext.vue";
// Heavy video components - load async
const RegisterVideo = defineAsyncComponent(
    () => import("./components/RegisterVideo.vue"),
);
import InputTextarea from "./components/InputTextarea.vue";
import Input from "./components/Input.vue";
// Heavy editor - load async
const InputMce = defineAsyncComponent(
    () => import("./components/InputMce.vue"),
);

import InputSelect from "./components/InputSelect.vue";
import ReceiverSelectChange from "./components/widget/ReceiverSelectChange.vue";
import InputSwitch from "./components/InputSwitch.vue";
import AdvanceInputSwitch from "./components/AdvanceInputSwitch.vue";
import InputAutocomplete from "./components/InputAutocomplete.vue";
import InputAddress from "./components/InputAddress.vue";
import InputTag from "./components/InputTag.vue";
import InputDate from "./components/InputDate.vue";
import InputDateBirth from "./components/InputDateBirth.vue";
import InputFile from "./components/InputFile.vue";
import InputHidden from "./components/widget/InputHidden.vue";
import Multiple from "./components/Multiple.vue";
import Modal from "./components/Modal.vue";
import FormAjax from "./components/FormAjax.vue";
import ResizeImage from "./components/ResizeImage.vue";
import ModalConfirm from "./components/ModalConfirm.vue";
import ToggleShow from "./components/widget/ToggleShowComponent.vue";
// Heavy payment components - load async
const CreditCard = defineAsyncComponent(
    () => import("./components/widget/CreditCard.vue"),
);
const Payment = defineAsyncComponent(
    () => import("./components/widget/Payment.vue"),
);
// Heavy video upload - load async
const UploadVideo = defineAsyncComponent(
    () => import("./components/widget/UploadVideo.vue"),
);
const ApplyJobPost = defineAsyncComponent(
    () => import("./components/widget/ApplyJobPost.vue"),
);
const UserNotification = defineAsyncComponent(
    () => import("./components/UserNotification.vue"),
);
const PageLoading = defineAsyncComponent(
    () => import("./components/PageLoading.vue"),
);
const FiltersComponent = defineAsyncComponent(
    () => import("./components/widget/FiltersComponent.vue"),
);
const Tabs = defineAsyncComponent(() => import("./components/Tabs.vue"));
const Slider = defineAsyncComponent(
    () => import("./components/widget/Slider.vue"),
);
// Heavy chart - load async
const Chart = defineAsyncComponent(
    () => import("./components/widget/Chart.vue"),
);
const OverlayGallery = defineAsyncComponent(
    () => import("./components/OverlayGallery.vue"),
);
const RadioCustom = defineAsyncComponent(
    () => import("./components/widget/RadioCustom.vue"),
);
// Heavy map - load async
const Map = defineAsyncComponent(() => import("./components/Map.vue"));
const SpeechScriptInteractive = defineAsyncComponent(() => import("./components/SpeechScriptInteractive.vue"));
import Tooltip from "@/frontend/js/components/widget/Tooltip.vue";

const UserMenu = defineAsyncComponent(
    () => import("@/frontend/js/components/UserMenu.vue"),
);
const Card = defineAsyncComponent(
    () => import("@/frontend/js/components/Card.vue"),
);
const UnifiedAuth = defineAsyncComponent(
    () => import("@/frontend/js/components/UnifiedAuth.vue"),
);
const CandidateOnboarding = defineAsyncComponent(
    () => import("@/frontend/js/components/CandidateOnboarding.vue"),
);
const PreferenceToggle = defineAsyncComponent(
    () => import("@/frontend/js/components/PreferenceToggle.vue"),
);
const SalaryExpectations = defineAsyncComponent(
    () => import("@/frontend/js/components/SalaryExpectations.vue"),
);
const Skeleton = defineAsyncComponent(
    () => import("@/frontend/js/components/Skeleton.vue"),
);
const PdfPagePreview = defineAsyncComponent(
    () => import("@/frontend/js/components/PdfPagePreview.vue"),
);

import mitt from "mitt";

// --- Vue setup ---
const emitter = mitt();
const app = createApp({});

// Global event bus for Vue 3 compatibility
app.config.globalProperties.emitter = emitter;

// Polyfill for Vue 2 $on, $off, $once on the root instance
app.config.globalProperties.$on = function (...args) {
    emitter.on(...args);
};
app.config.globalProperties.$off = function (...args) {
    emitter.off(...args);
};
app.config.globalProperties.$once = function (event, handler) {
    const sifter = (arg) => {
        emitter.off(event, sifter);
        handler(arg);
    };
    emitter.on(event, sifter);
};
// Note: $emit is already available on components, but we add it here just in case for root.
app.config.globalProperties.$emit = function (...args) {
    emitter.emit(...args);
};

// Plugins
app.use(Notifications);
app.use(VCalendar, {
    componentPrefix: "vc", // optional, defaults to 'v'
    firstDayOfWeek: 2, // Monday
});

// Register components globally
app.component("generic-context", GenericContext);
app.component("register-video", RegisterVideo);
app.component("input-wf", Input);
app.component("textarea-wf", InputTextarea);
app.component("input-mce", InputMce);

app.component("input-switch", InputSwitch);
app.component("advance-input-switch", AdvanceInputSwitch);
app.component("input-select", InputSelect);
app.component("receiver-select", ReceiverSelectChange);
app.component("input-autocomplete", InputAutocomplete);
app.component("input-address", InputAddress);
app.component("input-tag", InputTag);
app.component("input-date", InputDate);
app.component("input-date-birth", InputDateBirth);
app.component("input-file", InputFile);
app.component("input-hidden", InputHidden);
app.component("multiple-wf", Multiple);
app.component("modal-wf", Modal);
app.component("form-ajax", FormAjax);
app.component("resize-image", ResizeImage);
app.component("modal-confirm", ModalConfirm);
app.component("credit-card", CreditCard);
app.component("payment-wf", Payment);
app.component("upload-video", UploadVideo);
app.component("apply-job-post", ApplyJobPost);
app.component("toggle-show", ToggleShow);
app.component("user-notification", UserNotification);
app.component("page-loading", PageLoading);
app.component("filters", FiltersComponent);
app.component("tabs-wf", Tabs);
app.component("slider", Slider);
app.component("chart", Chart);
app.component("overlay-gallery", OverlayGallery);
app.component("radio-custom", RadioCustom);
app.component("map-component", Map);
app.component("tooltip", Tooltip);
app.component("speech-script-interactive", SpeechScriptInteractive);
app.component(
    "location-selector",
    defineAsyncComponent(
        () => import("./components/widget/LocationSelector.vue"),
    ),
);

app.component("wf-card", Card);
app.component("user-menu", UserMenu);
app.component("unified-auth", UnifiedAuth);
app.component("candidate-onboarding", CandidateOnboarding);
app.component("preference-toggle", PreferenceToggle);
app.component("salary-expectations", SalaryExpectations);
app.component("wf-skeleton", Skeleton);
app.component("pdf-page-preview", PdfPagePreview);

// Mount Vue
app.mount("#app");

// --- Video.js removed for now ---
// (imports commented out above)
// (init code removed to avoid ReferenceErrors)
